import React, { useState } from 'react'
import { Card, Collapse, Spinner } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { IntegrationOperationFilter, OperationDirection, useRunOperationMutation } from '../../../../../store/integrations/integrations'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { TestResultDisplay } from '../TestResultDisplay'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { IntegrationSettings } from '../../AdminSettings/schema'
import { useFormContext } from 'react-hook-form'
import '../MappingTester.scss'

const MAX_TESTED_EVENTS = 20

type SyncResultWithEvents = {
  data?: {
    event_sync?: {
      data?: unknown[]
    }
  }
}

const hasEventData = (value: unknown): value is SyncResultWithEvents => typeof value === 'object' && value !== null

export interface CalendarSyncMappingTesterProps {
  serviceId: IntegrationServiceId
}

export const CalendarSyncMappingTester: React.FC<CalendarSyncMappingTesterProps> = ({ serviceId }) => {
  const [isOpen, setIsOpen] = useState(false)
  const {
    formState: { isDirty, isValid },
    watch,
  } = useFormContext<IntegrationSettings>()

  const isFormUnsaved = isDirty || !isValid
  const integrationName = getIntegrationDisplayName(serviceId)
  const eventObject = watch('mappings.calendar.objectName')
  const accountId = useAppSelector(selectAccountId)
  const [sync, { isLoading: syncing, data: syncResult, isSuccess: syncSuccess, isError: syncIsError, error: syncError }] = useRunOperationMutation()

  const handleSyncTest = async () => {
    setIsOpen(true)
    const testSyncMappingFilter: IntegrationOperationFilter = { object: `${eventObject}_sync`, platform: serviceId, direction: OperationDirection.INBOUND }
    await sync({ accountId, connector: serviceId, context: { max_object_count: MAX_TESTED_EVENTS }, operation: testSyncMappingFilter }).unwrap()
  }

  const syncedEvents: unknown[] = hasEventData(syncResult) && Array.isArray(syncResult.data?.event_sync?.data) ? (syncResult.data?.event_sync?.data ?? []) : []
  const hasNoEvents = syncSuccess && !syncIsError && syncedEvents.length === 0

  const isSuccess = syncSuccess && !syncIsError && !hasNoEvents
  const isError = syncIsError || hasNoEvents
  const errorMessage = hasNoEvents
    ? `No upcoming events came back from ${integrationName}. Please verify your object, field mappings and filters.`
    : `An unexpected error occurred while reading events from ${integrationName}. Please verify your integration settings and field mappings.`

  return (
    <Card className='zocks-border-0.75 border-1 shadow-sm border-dark'>
      <Card.Body className='p-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className='bi bi-arrow-repeat fs-5'></i>
              <h6 className='fw-bold mb-0'>Calendar Sync</h6>
            </div>
            <p className='text-secondary mb-0 small'>Confirm event records synchronize from your {integrationName} as configured.</p>
          </div>
          <Tooltip id='calendar-sync-run-test-tooltip' title={isFormUnsaved ? 'Save your changes before running a test' : undefined}>
            <Button
              type='button'
              variant='primaryGhost'
              className='d-flex align-items-center gap-2 fw-semibold flex-shrink-0'
              onClick={handleSyncTest}
              disabled={syncing || isFormUnsaved || !eventObject}
            >
              {syncing ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-play-fill' />}
              Run Test
            </Button>
          </Tooltip>
        </div>

        <Collapse in={isOpen}>
          <div>
            <TestResultDisplay
              isLoading={syncing}
              hasResult={syncSuccess || syncIsError}
              isSuccess={isSuccess}
              isError={isError}
              integrationName={integrationName}
              rawResult={syncIsError ? syncError : syncResult}
              successContent={<p className='mb-0 text-dark ps-4'>Events are read from {integrationName} as configured.</p>}
              errorContent={<p className='mb-0 text-dark ps-4'>{errorMessage}</p>}
            />
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  )
}
